namespace cdh.viewModels {
    /** Minimal Type for EnumMetadata */
    export type EnumDefinitionBase<T, TName extends string = string> = {
        readonly value: T;
        readonly name: TName;
        readonly altNames?: string[];

        readonly displayName: string | KnockoutComputed<string>;
    };

    export function toLookup<T, TDef extends EnumDefinitionBase<T> = EnumDefinitionBase<T>>(values: TDef[]): { [key: string]: TDef } {
        let result: { [key: string]: TDef } = {};

        if (values)
            values.forEach(type => {
                var val: T | string = type.value;

                if (typeof val !== "string")
                    val = String(val);

                result[val.toUpperCase()] = type;
                result[type.name.toUpperCase()] = type;

                if (type.altNames)
                    for (var i = 0; i < type.altNames.length; i++) {
                        var an = type.altNames[i];
                        if (typeof an == "string" && an) {
                            var an2 = an.toUpperCase();
                            if (result[an2] && result[an2] != type)
                                throw new Error(`Alternative Name causes Conflict. Name: '${an}' For: '${type.name}' Assigned Value: '${result[an2].name}'`);
                            result[an2] = type;
                        }
                    }
            });

        return result;
    }

    export function fromLookup<T, TDef extends EnumDefinitionBase<T> = EnumDefinitionBase<T>>(lookup: { [key: string]: TDef }, key: string | number | T, fallback?: TDef): TDef {
        if (typeof (key) === "number") {
            var type = fromLookup<T, TDef>(lookup, String(key));
            if (type) return type;
            if (key < 0 || key > 9)
                key = String.fromCharCode(key);
            else
                key = null;
        }
        var strKey: string;

        if (key) {

            if (typeof (key) !== "string")
                key = String(key);

            strKey = key.toUpperCase();
        }

        var type = lookup[strKey];

        if (type)
            return type;
        else
            return fallback;
    }

    export function getEnumDefFunc<T extends number, TName extends string = string>(names: { [value: number]: TName }, prefix: string): { (val: T): EnumDefinitionBase<T, TName> } {

        return v => {

            var name = <TName>names[v];

            var res: EnumDefinitionBase<T, TName> = {
                value: v,
                name: name,
                displayName: t2(prefix + utils.firstToLowerCase(name))
            };

            return res;
        }

    }

    /** Lookup für EnumMetadata */
    export class EnumLookup<T, TName extends string = string, TDef extends EnumDefinitionBase<T, TName> = EnumDefinitionBase<T, TName>> implements Iterable<TDef>{

        private readonly lookup: { [key: string]: TDef; };
        constructor(public readonly values: TDef[], protected readonly fallbackVal?: TDef) {
            this.lookup = toLookup(values);
        }

        static createLookup<U, UName extends string = string, UDef extends EnumDefinitionBase<U, UName> = EnumDefinitionBase<U, UName>>(defFunc: { (value: U): UDef }, values: U[], fallback?: U) {

            var fallBackDef: UDef = null;
            var values2 = values.map(defFunc);

            if (fallback != undefined)
                fallBackDef = values2.find(v => v.value === fallback) || defFunc(fallback);

            var lookup: EnumLookup<U, UName, UDef> = new EnumLookup(values2, fallBackDef);

            return utils.extend2(lookup, lookup.getMapping());
        }

        public getDefinition(key: string | number | T) {
            return fromLookup(this.lookup, key, this.fallbackVal);
        }

        public get<prop extends keyof TDef>(key: string | number | T, prop: prop) {
            var def = fromLookup(this.lookup, key, this.fallbackVal);

            return def != null ? def[prop] : null;
        }

        public getValue(key: string | number | T): T {
            return this.get(key, "value");
        }

        public getName(key: string | number | T): TName {
            return this.get(key, "name");
        }

        public getDisplayName(key: string | number | T): string | KnockoutComputed<string> {
            var def = fromLookup(this.lookup, key, this.fallbackVal);

            return def != null ? def.displayName : null;
        }

        [Symbol.iterator](): Iterator<TDef> {
            return this.values[Symbol.iterator]();
        }

        public getMapping(): { readonly [keys in TName]: TDef } {
            var tmp: { [keys in TName]?: TDef } = {};

            this.values.forEach(v => { if (v.name) tmp[v.name] = v; });
            if (this.fallbackVal && this.fallbackVal.name && tmp[this.fallbackVal.name] === undefined)
                tmp[this.fallbackVal.name] = this.fallbackVal;

            return <{ [keys in TName]: TDef }>tmp;
        }
    }

    /** Base Class for ViewModels */
    export abstract class ViewModelBase<TVM, TM extends MappableObject = MappableObject, TParent = MappableBlock> implements MappableBlock<TM> {

        constructor(protected readonly _p: PropertyMapper<TVM, TM>, protected readonly parent: TParent, model?: Partial<TM>) {

            this.init();

            if (model !== undefined)
                this.fromModel(model);
        };

        public readonly init: () => void;
        public readonly fromModel: (mHeader: RecursivePartial<TM>) => void;
        public readonly toModel: () => TM;
    }

    export class PropertyMappedObject {
        public readonly _p: PropertyMapper<any>;

        public init() {
            this._p.create(this);
        }

        public fromModel(model: MappableObject) {
            let self = this;

            this._p.fromModel(this, model);
        };

        public toModel() {
            let self = this;
            let model: MappableObject = {};

            this._p.toModel(this, model);

            return model;
        };
    }

    utils.applyMixins(ViewModelBase, [PropertyMappedObject]);

    // #region SearchType
    export enum StringSearchTypeValues {
        Contains = "c",
        Equals = "eq",
        ContainsNot = "cn",
        BeginsWith = "bw",
        EndsWith = "ew"
    }
    export type StringSearchTypeNames = "Contains" | "Equals" | "ContainsNot" | "BeginsWith" | "EndsWith";

    export const StringSearchTypes: { [P in StringSearchTypeNames]: StringSearchTypeValues } = {
        Contains: StringSearchTypeValues.Contains,
        Equals: StringSearchTypeValues.Equals,
        ContainsNot: StringSearchTypeValues.ContainsNot,
        BeginsWith: StringSearchTypeValues.BeginsWith,
        EndsWith: StringSearchTypeValues.EndsWith
    }
    // #endregion

    // #region common Search Functions/Types/DataSources/DialogViewModels

    export interface ISearchViewModel {
        /** Type of the Search */
        readonly type: ObservableOrValue<StringSearchTypeValues>;
        /** Term to Search */
        readonly term: ObservableOrValue<string>;
    }

    export interface ISearchableViewModel extends ISearchViewModel {
        readonly recentTerms: KnockoutObservable<string[]>;
        /** Internal name for loading/storing recently used Terms */
        readonly searchName: KnockoutObservable<string>;

        /** Observable with the currently active SearchTerms */
        readonly currentSearch: KnockoutObservable<ISearchViewModel>;

        search(): Promise<void>;
    }

    /** A ViewModel that wraps around an DevExtreme-DataSource */
    export interface IDataSourceViewModel<TModel, TKey> {
        /** Instance of the DevExtreme DataSource */
        source: DevExpress.data.DataSource;

        /** Manager for attaching commands */
        readonly commandHandler: utils.MultiCommandManager<TKey>;
    }

    /** A ViewModel that combines a DevExtreme-DataSource with a Search Function */
    export interface ISearchableDataViewModel<TModel, TKey> extends IDataSourceViewModel<TModel, TKey>, ISearchableViewModel {

    }

    export interface ISearchableDataSource<TModel, TKey> extends ISearchableDataViewModel<TModel, TKey> {

        readonly type: KnockoutObservable<StringSearchTypeValues>;
        readonly term: KnockoutObservable<string>;
        readonly termGroup: KnockoutReadonlyComputed<utils.IRecentTermsGroup>;
        readonly recentTerms: KnockoutObservable<string[]>;
        readonly commandHandler: utils.MultiCommandManager<TKey>;

        readonly currentSearch: KnockoutObservable<ISearchViewModel>;
        readonly source: DevExpress.data.DataSource;

        search(): Promise<void>;
        addUserData(options: DevExpress.data.LoadOptions): void

        convertKey?(key: Partial<TModel>): TKey
    }

    /** Model for a SearchField in a multi Field search */
    export interface IFieldSearchModel {
        field: string;
        searchType?: StringSearchTypeValues;
        searchTerm: string
    }

    /**
     * Appends Search Parameters to the RouteData-Object
     * @param search ViewModel containing the Search-Data
     * @param params (optional) RouteData-Object to Expand
     */
    export function getSearchParams(search: ISearchViewModel, params?: Dictionary<string | number>): Dictionary<string | number> {
        params = params || {};

        if (search) {
            var term = ko.unwrap(search.term)
            let type = ko.unwrap(search.type);

            if (term) {
                params.s = term;
                params.q = type;
            }
        }

        return params;
    }

    /**
     * Appends multi field search Parameters to the RouteData-Object
     * @param obj RouteData-Object to Expand
     * @param fields Array of Search Fields
     */
    export function addMultifieldSearchParams(obj: MappableObject, fields: ObservableOrValueModel<IFieldSearchModel>[]) {
        if (!obj)
            obj = {};

        var i = 0;
        if (fields)
            fields.forEach(f => {
                var fi = ko.unwrap(f.field);
                var st = ko.unwrap(f.searchType);
                var t = ko.unwrap(f.searchTerm);

                if (fi) {
                    obj["f[" + i + "][n]"] = fi;
                    if (st != StringSearchTypeValues.Contains)
                        obj["f[" + i + "][q]"] = st;

                    if (t)
                        obj["f[" + i + "][s]"] = t;
                    i++;
                }
            });

        return obj;
    }

    /**
     * Modifies the URL by adding/replacing the search parameters
     * @param baseUrl current URL
     * @param search search parameters
     */
    export function getSearchUrl(baseUrl: string, search: ISearchViewModel): string {
        let url = baseUrl;

        if (search) {
            let type = ko.unwrap(search.type);
            let term = ko.unwrap(search.term);

            url = utils.updateUrlParameter(url, "s", term);
            url = utils.updateUrlParameter(url, "q", type);
        }

        return url;
    }

    /**
     * Appends Search Parameters to the Data-Object
     * @param search ViewModel containing the Search-Data
     * @param data  (AJAX-)Data-Object to Expand
     */
    export function getSearchData(search: ISearchViewModel, data?: { s?: string, q?: StringSearchTypeValues }) {
        data = data || {};

        if (search) {
            data.s = ko.unwrap(search.term);
            data.q = ko.unwrap(search.type);
        }

        return data;
    }

    export function handleCommandBase($dgc: JQuery<HTMLElement>, commandHandler: (cmd: string, key: any, elm: HTMLElement) => void) {
        $dgc.on("click.dx", "[data-cmd]", function () {

            let $this = $(this);
            let cmd = $this.data("cmd");
            let id = $this.closest("[data-id]").data("id");
            let url = "";

            commandHandler(cmd, id, this);
        });
    }

    export class ForbiddenError extends Error {

        constructor(msg: string) {
            super(msg);
            this.name = "ForbiddenError";
        }

    }

    export class DataError extends Error {

        constructor(msg: string, public readonly status?: string | number, public readonly msgArr?: KeyValuePair<string, ModelStateError[]>[]) {
            super(msg);
            this.name = "DataError";
        }

    }

    /** Basic Implementation for ISearchableDataSource */
    export type SearchableDataSourceCommonOptions<TSearchOptions> = {
        searchOptions?: TSearchOptions,
        reloadOnOptionsChanged?: boolean,
    }

    export type SearchableDataSourceLoadOptions<TModel, TSearchOptions> = {
        baseUrl: ((val: TSearchOptions) => string) | string | KnockoutReadonlyObservable<string>,
        key: DataSourceKey<TModel>,
        beforeLoad?: (ajax: JQuery.AjaxSettings, searchOptions: TSearchOptions) => void,
    }

    export type SearchableDataSourceOptions<TModel, TSearchOptions> = (SearchableDataSourceLoadOptions<TModel, TSearchOptions> /*| SearchableDataSourceStoreOptions*/) & SearchableDataSourceCommonOptions<TSearchOptions>

    export class SearchableDataSource<TModel, TKey, TSearchOptions = void> implements ISearchableDataSource<TModel, TKey> {

        readonly type: KnockoutObservable<StringSearchTypeValues>;
        readonly term: KnockoutObservable<string>;
        readonly fields: KnockoutObservableArray<IFieldSearchModel>;
        readonly termGroup: KnockoutComputed<utils.IRecentTermsGroup>;
        readonly recentTerms: KnockoutObservable<string[]>;
        readonly commandHandler: utils.MultiCommandManager<TKey>;

        readonly searchName = ko.observable("");
        readonly rowCount = ko.observable<number>(0);

        readonly currentSearch: KnockoutObservable<ISearchViewModel> = ko.observable();
        readonly source: DevExpress.data.DataSource;
        readonly searchOptions: TSearchOptions;
        reloadAction: utils.DelayedAction;

        constructor(options: SearchableDataSourceOptions<TModel, TSearchOptions>, type?: StringSearchTypeValues, term?: string) {

            let self = this;

            this.type = ko.observable<StringSearchTypeValues>(type || StringSearchTypes.Contains);
            this.term = ko.observable<string>(term);
            this.fields = ko.observableArray<IFieldSearchModel>();

            this.termGroup = ko.pureComputed(() => utils.terms.getGroup(ko.unwrap(this.searchName)));
            this.recentTerms = ko.pureComputed(() => this.termGroup().terms());
            this.commandHandler = new utils.MultiCommandManager();

            if (options.searchOptions)
                this.searchOptions = options.searchOptions;

            let bUrl: ObservableOrValueReadonly<string>;

            if (typeof options.baseUrl == "string") {
                bUrl = options.baseUrl;
            }
            else if (typeof options.baseUrl == "function") {
                if (ko.isSubscribable(options.baseUrl))
                    bUrl = options.baseUrl
                else {
                    var urlFunc = options.baseUrl
                    bUrl = ko.computed(() => urlFunc(ko.unwrap(self.searchOptions)));
                }
                bUrl.subscribe(() => self.source.reload());
            }
            else
                throw new Error("Invalid BaseUrl");

            var store = new DevExpress.data.CustomStore({
                key: options.key,
                load: async function (o) {
                    var url = ko.unwrap(bUrl);
                    if (!url) return null; // Q> Return empty result(Array) or throw error?

                    var params = getLoadParams(o);
                    getSearchParams(ko.unwrap(self.currentSearch), params);


                    let fsArr = ko.unwrap(self.fields);
                    if (fsArr && fsArr.length)
                        addMultifieldSearchParams(params, fsArr);


                    var settings: JQuery.AjaxSettings = {
                        url: url,
                        method: "get",
                        data: params
                    };

                    if (self.beforeLoad)
                        self.beforeLoad(settings);
                    if (options.beforeLoad)
                        options.beforeLoad(settings, ko.unwrap(self.searchOptions));

                    return $.ajax(settings).pipe(utils.unwrapActionResponse, utils.getError);
                }
            });

            var sourceOptions: DevExpress.data.DataSourceOptions = {
                store: store,
                requireTotalCount: true,
                paginate: true,
                pageSize: 20,
            };

            if (this.prepareSourceOptions)
                this.prepareSourceOptions(sourceOptions);

            this.source = new DevExpress.data.DataSource(sourceOptions);
            this.reloadAction = new utils.DelayedAction(() => self.source.reload(), 250);

            if (options.beforeLoad && options.reloadOnOptionsChanged) {
                var cmp = ko.computed(() => {
                    // Get a Dependency on everything that could trigger a reload
                    var ajax: JQuery.AjaxSettings = { url: ko.unwrap(bUrl) }
                    options.beforeLoad(ajax, self.searchOptions);

                    self.reloadAction.schedule();
                }).extend({ deferred: true });

            }

            this.source.on("loadingChanged", function () {
                self.rowCount(self.source.totalCount());
            });
        }

        protected prepareSourceOptions?(options: DevExpress.data.DataSourceOptions): void;

        protected beforeLoad?(ajax: JQuery.AjaxSettings<any>): void;

        public addUserData(options?: DevExpress.data.LoadOptions): void {
            options = options || this.source.loadOptions();

            let s = { type: ko.unwrap(this.type), term: ko.unwrap(this.term) };
            this.currentSearch(s);
            utils.terms.addRecentTerm(ko.unwrap(this.searchName), s.term);
            options.userData = getSearchData(s);
        }

        public search() {
            this.addUserData();
            this.source.pageIndex(0);

            return this.source.reload();
        }
    }

    export class GridDefaultCommandHandler implements ICommandHandler<any>{
        constructor(readonly searcher: DialogSearcherViewModelBase<any, any> | ISearchableDataSource<any, any>) {

        }

        static readonly defaultCommand = "default";
        redirectCommand?: ObservableOrValueReadonly<string>;


        async handleCommand(ctx: ICommandContext<any>): Promise<void> {
            if (utils.equalsIgnoreCase(ctx.cmd, "default")) {
                ctx.handled = true;

                var arg = ctx.arg;
                if (this.searcher.convertKey)
                    arg = this.searcher.convertKey(ctx.arg);

                var cmd = ko.unwrap(this.redirectCommand);
                if (cmd) {
                    let data = utils.copy(ctx.data) || {};
                    data._originalContext = ctx;
                    await this.searcher.commandHandler.sendCommand(cmd, arg, ctx.element, data);

                    return;
                }

                if (this.searcher instanceof DialogSearcherViewModelBase) {
                    var setResult = true;

                    switch (this.searcher.selectionMode()) {
                        case "none":
                            setResult = false;
                    }

                    if (setResult)
                        this.searcher.dialogResult(true);
                }
            }
        }
    }

    /** Base class for SelectionDialogs */
    export abstract class DialogSearcherViewModelBase<TModel, TKey>
    {
        private resultHandler: JQueryCallback;

        public abstract source: DevExpress.data.DataSource
        public readonly commandHandler: utils.MultiCommandManager<TKey>;
        public readonly defaultCommand: GridDefaultCommandHandler;
        public readonly selected = ko.observableArray<TKey>();
        public readonly focused = ko.observable<TKey>();
        public readonly multiSelection = ko.observable<boolean>(false);
        public readonly selectionMode = ko.observable<"single" | "multi" | "none">("single");
        public readonly selectedItems = ko.observableArray<TModel>();
        public readonly dialogResult = ko.observable<boolean>();
        public readonly isSearching = ko.observable(false);
        public readonly actions = ko.observableArray<IActionDefinition>();

        convertKey(key: TKey | Partial<TModel>): TKey { return key as TKey; }

        constructor() {
            this.resultHandler = $.Callbacks();
            this.commandHandler = new utils.MultiCommandManager();
            this.commandHandler.addHandlers(this.defaultCommand = new GridDefaultCommandHandler(this));
        }

        public addResultHandler(handler: (key: TKey, item?: TModel) => void) {
            this.resultHandler.add(handler);
        }

        public removeResultHandler(handler: (key: TKey, item?: TModel) => void) {
            this.resultHandler.remove(handler);
        }

        public async showDialog(multi: "none"): Promise<void>;
        public async showDialog(multi: false | "single"): Promise<{ key: TKey, data: TModel }>;
        public async showDialog(multi: true | "multi"): Promise<{ keys: TKey[], data: TModel[] }>;
        public async showDialog(popup?: IDialogContainerViewModel, multi?: false | "single"): Promise<{ key: TKey, data: TModel }>;
        public async showDialog(popup: IDialogContainerViewModel, multi: true | "multi"): Promise<{ keys: TKey[], data: TModel[] }>;
        public async showDialog(popup: IDialogContainerViewModel, multi: "none"): Promise<void>;
        public async showDialog(arg0: IDialogContainerViewModel | boolean | string, arg1?: boolean | string) {

            var popup: IDialogContainerViewModel = null;
            var _m: boolean | string = false;

            if (typeof (arg0) == "string" || typeof (arg0) == "boolean") {
                _m = arg0;
            }
            else {
                popup = arg0;
                _m = arg1;
            }


            let self = this;
            let d: { dispose(): void };

            let mode: "single" | "multi" | "none" = "single";
            let rf: (m: this) => any;
            if (_m === true) mode = "multi";
            else if (typeof (_m) == "string") {
                if (utils.equalsIgnoreCase(_m, "none"))
                    mode = "none";
                else if (utils.equalsIgnoreCase(_m, "multi"))
                    mode = "multi";
            }



            if (popup == null) {
                let tmp = ui.appendDialog();
                popup = tmp;
                d = tmp;
            }

            try {
                self.multiSelection(mode == "multi");
                self.selectionMode(mode);

                switch (mode) {
                    case "single":
                        rf = m => ({ key: self.convertKey(m.selected()[0]), data: m.selectedItems()[0] });
                        break;
                    case "multi":
                        rf = m => ({ keys: m.selected().map(self.convertKey), data: m.selectedItems() });
                        break;
                    default:
                }

                let r = await popup.showDialogValue<this, any>(this.getDialogOptions(), self, rf);
                if (r && mode == "single")
                    self.resultHandler.fire(r.key, r.data)

                return r;
            } catch (err) {
                debugger;
                if (mode != "none")
                    throw err;
            }
            finally {
                if (d)
                    d.dispose();
            }
        }

        // #region DialogOptions
        protected abstract getDialogOptions(): IDialogOptions;
        // #endregion

        showDialogKey(popup?: IDialogContainerViewModel) {
            return this.showDialog(popup).then(r => r.key);
        }
        showDialogData(popup?: IDialogContainerViewModel) {
            return this.showDialog(popup).then(r => r.data);
        }

        showDialogMulti(popup?: IDialogContainerViewModel) {
            return this.showDialog(popup, true);
        }

        showDialogMultiKeys(popup?: IDialogContainerViewModel) {
            return this.showDialogMulti(popup).then(r => r.keys);
        }


        public abstract addUserData(options: DevExpress.data.LoadOptions): void;

        public async search(): Promise<void> {
            let self = this;

            if (self.isSearching())
                return;

            self.isSearching(true);

            let options: DevExpress.data.LoadOptions = self.source.loadOptions() || { skip: 0, take: 20, requireTotalCount: true };


            try {
                this.addUserData(options);

                if (self.beforeSearch) {
                    if (!self.beforeSearch())
                        return;
                }

                self.source.pageIndex(0);
                await <Promise<void>>self.source.reload();
            }
            catch (reason) {
                DevExpress.ui.notify(reason, "error", 1200);
            }
            finally {
                self.isSearching(false);
            }
        };

        protected beforeSearch?(): boolean;
    }

    /** Base class for SelectionDialogs with Search Function */
    export abstract class DefaultDialogSearcherViewModelBase<TModel, TKey> extends DialogSearcherViewModelBase<TModel, TKey> implements ISearchableDataViewModel<TModel, TKey> {
        private readonly _ds: ISearchableDataSource<TModel, TKey>;
        readonly type: KnockoutObservable<StringSearchTypeValues>;
        readonly term: KnockoutObservable<string>;

        readonly searchName: KnockoutObservable<string>;
        readonly termGroup: KnockoutReadonlyComputed<utils.IRecentTermsGroup>;
        readonly recentTerms: KnockoutObservable<string[]>;

        readonly currentSearch: KnockoutObservable<ISearchViewModel>;
        readonly source: DevExpress.data.DataSource;

        constructor(ds: ISearchableDataSource<TModel, TKey>) {
            super();

            let self = this;
            this._ds = ds;

            if (ds.convertKey) this.convertKey = ds.convertKey.bind(ds);

            this.type = this._ds.type;
            this.term = this._ds.term;


            this.searchName = this._ds.searchName;
            this.termGroup = this._ds.termGroup;
            this.recentTerms = this._ds.recentTerms;

            this.currentSearch = this._ds.currentSearch;

            this.source = this._ds.source;

            //this.commandHandler.importHandlers(ds.commandHandler);
            this.commandHandler.setParent(ds.commandHandler);
        }

        public addUserData(options: DevExpress.data.LoadOptions): void {
            this._ds.addUserData(options);
        }

        // #region DialogOptions
        protected langId?: string
        protected getTitle() {
            if (this.langId)
                return t(this.langId);
            else
                return "";

        }
        protected abstract getTemplate(): DialogTemplate;
        protected getDialogOptions(): IDialogOptions {
            return { template: this.getTemplate(), title: this.getTitle(), size: "default" }
        }
        // #endregion
    }

    /** Simple Implementation for SelectionDialog with Search Function */
    export class DialogSearcherViewModel<TModel, TKey> extends DialogSearcherViewModelBase<TModel, TKey> implements ISearchableDataViewModel<TModel, TKey> {
        readonly dataSource: ISearchableDataSource<TModel, TKey>;
        readonly type: KnockoutObservable<StringSearchTypeValues>;
        readonly term: KnockoutObservable<string>;

        readonly searchName: KnockoutObservable<string>;
        readonly termGroup: KnockoutReadonlyComputed<utils.IRecentTermsGroup>;
        readonly recentTerms: KnockoutObservable<string[]>;

        readonly currentSearch: KnockoutObservable<ISearchViewModel>;
        readonly source: DevExpress.data.DataSource;

        constructor(ds: ISearchableDataSource<TModel, TKey>, readonly dialogOptions: IDialogOptions) {
            super();

            let self = this;
            this.dataSource = ds;

            if (ds.convertKey) this.convertKey = ds.convertKey.bind(ds);

            this.type = this.dataSource.type;
            this.term = this.dataSource.term;
            this.currentSearch = this.dataSource.currentSearch;

            this.searchName = this.dataSource.searchName;
            this.termGroup = this.dataSource.termGroup;
            this.recentTerms = this.dataSource.recentTerms;

            this.source = this.dataSource.source;

            //this.commandHandler.importHandlers(ds.commandHandler);
            this.commandHandler.setParent(ds.commandHandler);

            this.source.on("loadError", (e: Error) => {
                if (e.name == "ForbiddenError") {
                    var msg = t("view/error/functionNotAvailable");
                    cdh.ui.alertIcon(msg, "fa-exclamation-triangle text-danger");
                    self.dialogResult(false);
                }
            });
        }

        public addUserData(options: DevExpress.data.LoadOptions): void {
            this.dataSource.addUserData(options);
        }

        protected getDialogOptions(): IDialogOptions {
            return this.dialogOptions;
        }
    }

    export class GenericSearcherViewModel<TModel, TKey> extends DialogSearcherViewModel<TModel, TKey>{
        readonly gridOptions: DevExpress.ui.dxDataGridOptions
        // Enable In-Grid-Editing
        readonly edit: boolean;
        searchOptionsTemplate?: DialogTemplate;

        showSearch?: ObservableOrValueReadonly<boolean>;
        autoFocus?: ObservableOrValueReadonly<boolean>;

        static readonly dialogTemplate: DialogTemplate = { type: "id", value: "templates/core/generic-search-dialog" };

        // Q> How should I define the Parameters for this Constructor?
        constructor(ds: ISearchableDataSource<TModel, TKey>, title: ObservableOrValueReadonly<string>, gridOptions: SearchGridOptions) {

            super(ds, {
                template: GenericSearcherViewModel.dialogTemplate,
                title: title,
                size: "default",
            });

            let go: DevExpress.ui.dxDataGridOptions = this.gridOptions = {
                remoteOperations: true,
                dataSource: this.source,
                columns: gridOptions.cols.slice(),

                customizeColumns: gridOptions.onCustomizeColumns || getCustomizeColumnsHandler(gridOptions.columnCustomizationOptions)
            };

            if (gridOptions.onEditorPreparing) {
                go.onEditorPreparing = gridOptions.onEditorPreparing;
            }
            if (gridOptions.onRowPrepared) {
                go.onRowPrepared = gridOptions.onRowPrepared;
            }

            this.edit = gridOptions.edit || false;
            go.columns.push(ui.addDataGridButtonColumn({}, this.commandHandler, gridOptions.commands, gridOptions.cmdDataFunc, this.convertKey));
            if (gridOptions.newCommandToolbarButton) {
                go.onToolbarPreparing = ui.addNewCommandToolbarButton(this.commandHandler);
            }

            if (gridOptions.actions) {
                if ($.isArray(gridOptions.actions))
                    this.actions.push(...gridOptions.actions);
                else if (typeof gridOptions.actions == "function")
                    this.actions.push(...gridOptions.actions(this));
            }
        }
    }

    // #region Non-Ajax Searchs (Redirect)
    /** ViewModel for a Default Search by Redirect */
    export class GeneralSearchViewModel implements ISearchViewModel {
        handler: (url: string) => boolean;
        readonly type: KnockoutObservable<StringSearchTypeValues>;
        readonly term: KnockoutObservable<string>;

        readonly searchName: KnockoutObservable<string>;
        readonly recentTerms: KnockoutObservable<string[]>;
        readonly termGroup: KnockoutComputed<utils.IRecentTermsGroup>;

        public readonly actions = ko.observableArray<IActionDefinition>();

        constructor(private url: string, searchName?: string, handler?: { (url: string): boolean }) {
            this.type = ko.observable<StringSearchTypeValues>(StringSearchTypes.Contains);
            this.term = ko.observable<string>("");
            this.searchName = ko.observable(searchName || "");

            this.termGroup = ko.pureComputed(() => utils.terms.getGroup(ko.unwrap(this.searchName)));
            this.recentTerms = ko.pureComputed(() => this.termGroup().terms());

            this.handler = handler;
        }

        public search(): void {

            let url = getSearchUrl(this.url, this);
            this.termGroup().addTerm(ko.unwrap(this.term));
            //utils.terms.addRecentTerm(ko.unwrap(this.searchName), ko.unwrap(this.term));

            if (!this.handler || this.handler(url))
                document.location.href = url;
        };

        //public extendedSearch(): Promise<null> {
        //    return ui.showPopup(this, { template: { type: "template", value: "search" }, title: t("data/common/search") });
        //}
    }

    export function getCurrentLocationAsync() {

        var d = $.Deferred<{ latitude: number, longitude: number }>();
        if (navigator.geolocation)
            navigator.geolocation.getCurrentPosition(function (pos) {
                d.resolve(pos.coords);
            }, function (err) {
                console.log(err);


                var msg: string;

                if (err.code == (err.PERMISSION_DENIED || 1))
                    msg = t("view/areaSearch/geolocation/permissionDenied");
                else if (err.code == (err.POSITION_UNAVAILABLE || 2))
                    msg = t("view/areaSearch/geolocation/positionUnavailable");
                else if (err.code == (err.TIMEOUT || 3))
                    msg = t("view/areaSearch/geolocation/timeout");
                else {
                    msg = t("view/areaSearch/geolocation/error");
                    if (err.message)
                        msg += "\n(" + err.message + ")";
                }
                d.reject(new Error(msg));
            }, { enableHighAccuracy: false, timeout: 5000 });
        else {
            d.reject(new Error(t("view/areaSearch/geolocation/notSupported")));
        }

        return d.promise();
    }

    /** ViewModel for an Area Search by Redirect */
    export class AreaSearchViewModel {

        radius: KnockoutObservable<number>;
        longitude: KnockoutObservable<number>;
        latitude: KnockoutObservable<number>;

        constructor(private url: string, radius?: number, lat?: number, lon?: number) {
            this.radius = ko.observable(radius);
            this.latitude = ko.observable(lat);
            this.longitude = ko.observable(lon);
        }

        private static _updateCoords(self: AreaSearchViewModel, coords: { latitude: number, longitude: number }) {
            self.latitude(coords.latitude);
            self.longitude(coords.longitude);
        };

        private static _current(self: AreaSearchViewModel, skipSearch: boolean) {

            let p = getCurrentLocationAsync().then(
                p => {
                    AreaSearchViewModel._updateCoords(self, p);

                    if (!skipSearch)
                        self.search();
                },
                err => {
                    DevExpress.ui.notify(err.message, "warning", 1200)
                });

            isBusy.setBusy(p, t("view/areaSearch/geolocation/pending"));
        };

        public search() {

            let uri = this.url;
            let lat = ko.unwrap(this.latitude);
            let lon = ko.unwrap(this.longitude);
            let rad = ko.unwrap(this.radius);

            if (isNaN(lat) || isNaN(lon))
                return;

            uri = utils.updateUrlParameter(uri, "latitude", lat);
            uri = utils.updateUrlParameter(uri, "longitude", lon);

            if (!isNaN(rad) && rad > 0) {
                uri = utils.updateUrlParameter(uri, "radius", rad);
            }

            document.location.href = uri;
        };

        //public extendedSearch() {
        //    return ui.showPopup(this, { template: { type: "template", value: "areaSearch" }, title: t("view/areaSearch/title") });
        //}

        public currentPos() { AreaSearchViewModel._current(this, true) };
        public searchCurrent() { AreaSearchViewModel._current(this, false) };

    }
    // #endregion
    // #endregion

    // #region EditModels

    export type EditorMode = "create" | "edit" | "details" | "delete" | "none";

    /** CommandHandler that forwards Edit-Commands to an Editor */
    export class EditorCommandHandler<TKey, TArg = TKey> implements ICommandHandler<TArg> {

        readonly editor = ko.observable<EditorViewModelBase<any, any, TKey>>();
        readonly searcher = ko.observable<DialogSearcherViewModelBase<any, TArg>>();

        constructor(editor?: EditorViewModelBase<any, any, TKey>, searcher?: DialogSearcherViewModelBase<any, TArg>, private createKey?: Partial<TKey>, private convertArg?: (arg: TArg) => TKey) {

            this.editor(editor);
            this.searcher(searcher);
        }

        public handleCommand(ctx: ICommandContext<TArg>) {
            var e = ko.unwrap(this.editor);
            var retVal = false;

            if (e) {
                var s = ko.unwrap(this.searcher);

                switch (ctx.cmd) {

                    case "create":
                        e.create(undefined, <TKey>this.createKey);
                        retVal = true;
                        break;
                    case "edit":
                    case "details":
                        var key = this.convertArg ? this.convertArg(ctx.arg) : ctx.arg as unknown as TKey;

                        e.load(key, ctx.cmd);
                        retVal = true;
                        break;
                    default:
                }
            }

            if (retVal && s) {
                s.dialogResult(false);
            }

            ctx.handled = retVal;
            return Promise.resolve();
        }

    }

    export var enablePopupEditors = true;

    /** CommandHandler that forwards  Create/Edit-Commands to an Popup */
    export class PopupEditorCommandHandler<TRep extends IDataRepository<any, TKey>, TKey, TOwner = any, TArg = TKey> implements ICommandHandler<TArg> {

        readonly editor = ko.observable<EditorViewModelBase<any, any, TKey>>();
        readonly searcher: DialogSearcherViewModelBase<any, TKey | TArg> | SearchableDataSource<any, TKey | TArg>;


        constructor(private readonly rep: TRep, private readonly editorType: { new(rep: TRep, owner?: TOwner): EditorViewModelBase<any, any, TKey, any> },
            readonly options: IDialogOptions, private readonly owner: TOwner, searcher?: DialogSearcherViewModelBase<any, TKey | TArg> | SearchableDataSource<any, TKey | TArg>,
            private createKey?: TKey, private convertArg?: (arg: TArg) => TKey) {

            this.searcher = searcher;
        }

        deleteMode: EditorMode = "none";


        public handleCommand(ctx: ICommandContext<TArg>) {

            if (enablePopupEditors) {
                var retVal = false;
                switch (ctx.cmd) {

                    case "create":
                        var e = this.getEditor();
                        if (e) {
                            e.create(undefined, this.createKey);
                            retVal = true;
                        }
                        break;
                    case "edit":
                    case "details":
                        var e = this.getEditor();
                        if (e) {
                            var key = this.convertArg ? this.convertArg(ctx.arg) : ctx.arg as unknown as TKey;
                            e.load(key, ctx.cmd);
                            retVal = true;
                        }
                        break;
                    case "delete":
                        let delMode = this.deleteMode;
                        if (delMode == "delete" || delMode == "edit" || delMode == "details") {
                            var e = this.getEditor();
                            if (e) {
                                var key = this.convertArg ? this.convertArg(ctx.arg) : ctx.arg as unknown as TKey;
                                e.load(key, ctx.cmd);
                                retVal = true;
                            }
                        }
                        break;
                    default:
                }

                ctx.handled = retVal;
            }

            return Promise.resolve();
        }

        getSearchValue?: (m: any) => string;

        private getEditor() {
            var self = this;
            var e = ko.unwrap(self.editor);

            if (!e) {
                if (this.editorType)
                    e = new this.editorType(self.rep, self.owner);
                else if (this.rep.createEditor)
                    e = this.rep.createEditor(self.owner);
                else
                    return null;

                this.editor(e);

                var dia = ui.appendDialog();
                var editor = self.editor;
                var searcher = self.searcher;
                var o = utils.copy(self.options);

                //if (e.getPageInfo)
                //    o.pageInfo = e.getPageInfo();


                var close = () => {
                    dia.dispose();

                    var sv: string = null;
                    if (self.getSearchValue && ko.unwrap(e.dialogResult) && e.editMode() == "edit" || e.editMode() == "details") {
                        var m = ko.unwrap(e.model);
                        if (m)
                            try {
                                sv = self.getSearchValue(m);
                            }
                            catch { }
                    }

                    e.dispose();
                    editor(null);

                    if (searcher) {
                        if (sv && "term" in searcher) {
                            searcher.term(sv);
                            searcher.type(StringSearchTypeValues.Contains);

                        }


                        searcher.search();
                        //searcher.source.reload();
                    }
                }

                dia.showDialog(o, e).then(close, close);

            }

            return e;
        }
    }

    /** CommandHandler that forwards Create/Edit-Commands to a DataGrid */
    export class InGridEditorCommandHandler<TKey> implements ICommandHandler<TKey> {


        constructor(private readonly dg?: DevExpress.ui.dxDataGrid) {

        }

        public handleCommand(ctx: ICommandContext<TKey>) {
            var retVal = false;

            switch (ctx.cmd) {

                case "create":
                    var dg = this.getDataGrid(ctx.element);
                    if (dg) {
                        dg.addRow();
                        retVal = true;
                    }
                    break;
                case "edit":
                    var dg = this.getDataGrid(ctx.element);
                    if (dg) {
                        dg.editRow(this.getRow(dg, ctx.arg));
                        retVal = true;
                    }
                    break;
                case "delete":
                    var dg = this.getDataGrid(ctx.element);
                    if (dg) {
                        dg.deleteRow(this.getRow(dg, ctx.arg));
                        retVal = true;
                    }
                    break;
                default:
            }



            ctx.handled = retVal;
            return Promise.resolve();
        }
        getRow(dg: DevExpress.ui.dxDataGrid, key: TKey): number {

            var r = dg.getRowIndexByKey(key)

            if (r == -1)
                r = dg.getRowIndexByKey("" + key);

            return r
        }

        private getDataGrid(element: HTMLElement) {

            if (this.dg)
                return this.dg;

            try {
                return $(element).parents(".dx-widget").first().dxDataGrid("instance")
            }
            catch {
                return null;
            }

        }
    }

    /** CommandHandler that handles Delete-Commands*/
    export class DeleteCommandHandler<TKey> implements ISuccessCallbackCommandHandler<TKey, (key: TKey) => void> {

        static defaultPromptLangId = "view/confirm/delete";

        constructor(private readonly rep: IDataRepository<any, TKey>) {

            this.onSuccess = $.Callbacks();
        }

        errors?: ISendAjaxErrorHandler;
        prompt?: ObservableOrValueReadonly<string>;


        readonly onSuccess: JQuery.Callbacks<(key: TKey) => void>;


        async handleCommand(ctx: ICommandContext<TKey>) {

            if (utils.equalsIgnoreCase(ctx.cmd, "delete")) {
                ctx.handled = true;
                let self = this;


                if (await (DevExpress.ui.dialog.confirm(ko.unwrap(self.prompt) || t(DeleteCommandHandler.defaultPromptLangId), null) as Promise<boolean>)) {
                    try {
                        await self.rep.delete(ctx.arg, self.errors || utils.promptErrorHandler);
                        self.onSuccess.fire(ctx.arg);
                    }
                    catch (err) {
                        console.error("Error during delete", err);
                    }
                }
            }
        }
    }

    /** Basic Interface for DataRepository */
    export interface IDataRepository<TModel, TKey, TSearchModel = TModel, TViewModel = any> {

        createNew(key?: TKey): RecursivePartial<TModel>;
        get(k: TKey, errors?: ISendAjaxErrorHandler): Promise<TModel>;
        insert(m: TModel, errors?: ISendAjaxErrorHandler): Promise<TModel>;
        update(k: TKey, m: TModel, errors?: ISendAjaxErrorHandler): Promise<TModel>;
        delete(k: TKey, errors?: ISendAjaxErrorHandler): Promise<any>;

        createDataSource(type?: StringSearchTypeValues, term?: string): ISearchableDataSource<TSearchModel, TKey>;
        createEditor(owner?: any): EditorViewModelBase<TViewModel, TModel, TKey>;
    }

    /** Base Class for Data Repositories */
    export abstract class DataRepositoryBase<TModel, TKey, TSearchModel = TModel, TViewModel = any> implements IDataRepository<TModel, TKey, TSearchModel, TViewModel> {

        createNew(key?: TKey): RecursivePartial<TModel> { return {}; }
        abstract get(k: TKey): Promise<TModel>;
        abstract insert(m: TModel): Promise<TModel>;
        abstract update(k: TKey, m: TModel): Promise<TModel>;
        abstract delete(k: TKey): Promise<any>;

        abstract createDataSource(type?: StringSearchTypeValues, term?: string): ISearchableDataSource<TSearchModel, TKey>;
        abstract createEditor(owner?: any): EditorViewModelBase<TViewModel, TModel, TKey>;
    }

    type dxGridEditorPreparingEvent = { component?: DevExpress.ui.dxDataGrid, element?: DevExpress.core.dxElement, model?: any, parentType?: string, value?: any, setValue?: any, updateValueTimeout?: number, width?: number, disabled?: boolean, rtlEnabled?: boolean, cancel?: boolean, editorElement?: DevExpress.core.dxElement, readOnly?: boolean, editorName?: string, editorOptions?: any, dataField?: string, row?: DevExpress.ui.dxDataGridRowObject };
    type dxGridRowPreparedEvent = { component?: DevExpress.ui.dxDataGrid, element?: DevExpress.core.dxElement, model?: any, data?: any, key?: any, values?: Array<any>, columns?: Array<DevExpress.ui.dxDataGridColumn>, rowIndex?: number, rowType?: string, groupIndex?: number, isSelected?: boolean, isExpanded?: boolean, isNewRow?: boolean, rowElement?: DevExpress.core.dxElement };
    export type SearchGridOptions = {
        cols: DevExpress.ui.dxDataGridColumn[],
        // Q> Options ?
        edit?: boolean,
        newCommandToolbarButton?: boolean
        commands?: (CommandDefinition | DefaultCommands)[],
        cmdDataFunc?: (rowObj: any, data: Dictionary<any>) => void,
        actions?: IActionDefinition[] | { (s: ISearchableDataSource<any, any>): IActionDefinition[] }
        onEditorPreparing?(e: dxGridEditorPreparingEvent): void;
        onRowPrepared?(e: dxGridRowPreparedEvent): void;

        columnCustomizationOptions?: ColumnCustomizationOptions,
        onCustomizeColumns?: { (cols: DevExpress.ui.dxDataGridColumn[]): void },
    };



    export type DefaultColumnCustomizations = "date" | "time" | "datetime";
    export type ColumnCustomizationOptions = {
        before?: { (cols: DevExpress.ui.dxDataGridColumn[]): void };
        after?: { (cols: DevExpress.ui.dxDataGridColumn[]): void };
        cols?: {
            name?: string,
            type?: 'string' | 'number' | 'date' | 'boolean' | 'object' | 'datetime',
            cust: DefaultColumnCustomizations | { (col: DevExpress.ui.dxDataGridColumn): void },
            caption?: ObservableOrValueReadonly<string>
        }[];
    };

    function getDefaultColumnCustomizationFunc(name: DefaultColumnCustomizations): { (col: DevExpress.ui.dxDataGridColumn): void } {

        if (utils.equalsIgnoreCase(name, "date") || utils.equalsIgnoreCase(name, "time") || utils.equalsIgnoreCase(name, "datetime")) {
            var f = utils.getDateDxFormat(name);

            return c => c.format = f;
        }

        return null;
    }

    export function getCustomizeColumnsHandler(options?: ColumnCustomizationOptions): { (cols: DevExpress.ui.dxDataGridColumn[]): void } {

        if (!options) options = {
            cols: [
                { type: "date", cust: "date" },
                { type: "datetime", cust: "datetime" },
            ]
        };

        return cols => {
            if (options.before)
                options.before(cols);

            if (options.cols)
                for (var i = 0; i < options.cols.length; i++) {
                    var colOptions = options.cols[i];

                    if (!colOptions) continue;

                    var custFunc: { (col: DevExpress.ui.dxDataGridColumn): void } = null;
                    if (typeof colOptions.cust == "function")
                        custFunc = colOptions.cust;
                    else if (typeof colOptions.cust == "string")
                        custFunc = getDefaultColumnCustomizationFunc(colOptions.cust);


                    var hasFilter = false;
                    var tarCols: DevExpress.ui.dxDataGridColumn[] = cols;

                    if (colOptions.name) {
                        tarCols = tarCols.filter(c => utils.equalsIgnoreCase(c.name, colOptions.name));
                        hasFilter = true;
                    }
                    if (colOptions.type) {
                        tarCols = tarCols.filter(c => utils.equalsIgnoreCase(c.dataType, colOptions.type));
                        hasFilter = true;
                    }

                    if (hasFilter && tarCols.length) {
                        if (custFunc) {
                            tarCols.forEach(custFunc);
                        }
                        if (colOptions.caption) {
                            var cap = ko.unwrap(colOptions.caption);
                            tarCols.forEach(c => c.caption = cap);
                        }
                    }
                }

            if (options.after)
                options.after(cols);

        }
    }

    /** Base Class for Data Repositories with Functions for API Access */
    export abstract class ApiDataRepository<TModel, TKey, TSearchModel = TModel, TViewModel = any> extends DataRepositoryBase<TModel, TKey, TSearchModel, TViewModel> {

        getSearchName?(): string;
        abstract getKeyFields(): DataSourceKey<TSearchModel>
        abstract getSearchUrl(): string;
        abstract getGetUrl(key: TKey): string;
        abstract getInsertUrl(model?: TModel): string;
        getUpdateUrl(k: TKey): string { return this.getGetUrl(k); }
        getDeleteUrl(k: TKey): string { return this.getGetUrl(k); }

        get(code: TKey, errors?: ISendAjaxErrorHandler): Promise<TModel> {

            var url = this.getGetUrl(code);

            return utils.sendAjax<TModel>({
                url,
                method: "GET",
                errors
            });
        }

        insert(model: TModel, errors?: ISendAjaxErrorHandler): Promise<TModel> {
            var url = this.getInsertUrl(model);

            return utils.sendAjax<TModel>({
                url,
                method: "POST",
                errors,
                data: model
            });
        }
        update(key: TKey, model: TModel, errors?: ISendAjaxErrorHandler): Promise<TModel> {
            var url = this.getUpdateUrl(key);

            return utils.sendAjax<TModel>({
                url,
                method: "PUT",
                errors,
                data: model
            });
        }

        delete(key: TKey, errors?: ISendAjaxErrorHandler): Promise<void> {
            var url = this.getDeleteUrl(key);

            return utils.sendAjax<void>({
                url,
                method: "DELETE",
                errors
            });
        }

        prepareDataSourceCommands?(ds: IDataSourceViewModel<TSearchModel, TKey>): void;
        createDataSource(type?: StringSearchTypeValues, term?: string): ISearchableDataSource<TSearchModel, TKey> {
            let ds = new SearchableDataSource<TSearchModel, TKey>({ baseUrl: this.getSearchUrl(), key: this.getKeyFields() }, type, term);

            if (this.getSearchName)
                ds.searchName(this.getSearchName());

            if (this.prepareDataSourceCommands)
                this.prepareDataSourceCommands(ds);

            return ds;
        }

        abstract createEditor(owner?: any): EditorViewModelBase<TViewModel, TModel, TKey>;

        getTitle?(): ObservableOrValueReadonly<string>;
        getGridOptions?(): SearchGridOptions;

        createSearcher(): DialogSearcherViewModelBase<TSearchModel, TKey> {

            // TODO get Options for Searcher
            let title: ObservableOrValueReadonly<string> = null;

            if (this.getTitle) title = this.getTitle();
            if (!this.getGridOptions)
                throw new Error("getGridOptions not Implemented");

            let go = this.getGridOptions();

            let ds = this.createDataSource();

            let searcher = new GenericSearcherViewModel(ds, title, go);

            return searcher;
        }
    }

    function getLoadParams(loadOptions: DevExpress.data.LoadOptions, params?: Dictionary<string | number>): Dictionary<string | number> {

        params = params || {};

        if (loadOptions) {
            //Getting filter options
            if (loadOptions.filter) {
                params.filter = JSON.stringify(loadOptions.filter);
            }
            //Getting sort options
            if (loadOptions.sort) {
                params.sort = JSON.stringify(loadOptions.sort);
            }
            //Getting group options
            if (loadOptions.group) {
                params.group = JSON.stringify(loadOptions.group);
            }
            //skip and take are used for paging
            params.skip = loadOptions.skip; //A number of records that should be skipped
            params.take = loadOptions.take; //A number of records that should be taken

            //If the select expression is specified
            if (loadOptions.select) {
                params.select = JSON.stringify(loadOptions.select);
            }

            //If a user typed something in dxAutocomplete, dxSelectBox or dxLookup
            if (loadOptions.searchValue) {
                params.searchValue = loadOptions.searchValue;
                params.searchOperation = loadOptions.searchOperation;
                params.searchExpr = loadOptions.searchExpr as string;
            }
        }

        return params;
    }

    export class DataDataSource<TModel, TKey> implements ISearchableDataSource<TModel, TKey> {

        readonly type: KnockoutObservable<StringSearchTypeValues>;
        readonly term: KnockoutObservable<string>;
        readonly commandHandler: utils.MultiCommandManager<TKey>;

        readonly searchName = ko.observable("");
        readonly termGroup: KnockoutComputed<utils.IRecentTermsGroup>;
        readonly recentTerms: KnockoutObservable<string[]>;

        readonly currentSearch: KnockoutObservable<ISearchViewModel> = ko.observable();
        readonly source: DevExpress.data.DataSource;
        readonly allItems: KnockoutAsyncComputed<TModel[]>;

        getTitle?(): ObservableOrValueReadonly<string>;

        constructor(private readonly rep: ApiDataRepository<TModel, TKey, TModel, any>, type?: StringSearchTypeValues, term?: string) {

            let self = this;

            if (rep.getSearchName)
                this.searchName(rep.getSearchName());

            this.type = ko.observable<StringSearchTypeValues>(type || StringSearchTypes.Contains);
            this.term = ko.observable<string>(term);


            this.termGroup = ko.pureComputed(() => utils.terms.getGroup(ko.unwrap(this.searchName)));
            this.recentTerms = ko.pureComputed(() => this.termGroup().terms());
            this.commandHandler = new utils.MultiCommandManager();

            var baseUrl = ko.computed(() => self.rep.getSearchUrl());
            var s = ko.observable();

            // Q> Make Custom Store Options customisable?
            var load = function (o: DevExpress.data.LoadOptions): Promise<TModel[] | SearchResult<TModel>> {
                var url = ko.unwrap(baseUrl);

                var params = getLoadParams(o);
                getSearchParams(ko.unwrap(self.currentSearch), params);

                return $.getJSON(url, params)
                    .pipe(utils.unwrapActionResponse, utils.getError);

                //return utils.getList(url);
            }

            var store = new DevExpress.data.CustomStore({
                key: rep.getKeyFields(),
                load,
                byKey: function (key) {
                    return utils.sendAjax({
                        url: self.rep.getGetUrl(key),
                        method: "GET"
                    });
                },
                insert: async function (values: TModel) {
                    var res = await utils.sendAjax({
                        url: self.rep.getInsertUrl(values),
                        method: "POST",
                        data: values
                    });
                    s.valueHasMutated();
                    return res;
                },
                update: async function (key: TKey, values: TModel) {
                    var res = await utils.sendAjax({
                        url: self.rep.getUpdateUrl(key),
                        method: "PUT",
                        data: values
                    });
                    s.valueHasMutated();
                    return res;
                },
                remove: async function (key: TKey) {
                    var res = await utils.sendAjax({
                        url: self.rep.getDeleteUrl(key),
                        method: "DELETE"
                    });
                    s.valueHasMutated();
                    return res;
                }
            })

            this.source = new DevExpress.data.DataSource({
                store: store,
                requireTotalCount: true,
                paginate: true,
                pageSize: 10

            });

            baseUrl.subscribe(() => self.source.reload());

            this.allItems = utils.asyncComputed3({
                evaluator: async () => {
                    ko.unwrap(s);
                    var res = await load(null);
                    if (res && typeof (res) == "object" && (res as SearchResult<TModel>).data)
                        res = (res as SearchResult<TModel>).data;
                    if (!$.isArray(res))
                        res = [];

                    return res;
                }, owner: this,
                default: () => [] as TModel[],
                resetWhileLoading: false
            });

            if ("getTitle" in rep)
                this.getTitle = rep.getTitle.bind(rep);
        }

        protected prepareOptions(options: DevExpress.data.AspNet.AspNetStoreOptions) {

        }

        public addUserData(options?: DevExpress.data.LoadOptions): void {
            options = options || this.source.loadOptions();

            let s = { type: ko.unwrap(this.type), term: ko.unwrap(this.term) };
            this.currentSearch(s);
            utils.terms.addRecentTerm(ko.unwrap(this.searchName), s.term);
            options.userData = getSearchData(s);
        }

        public search() {
            this.addUserData();
            this.source.pageIndex(0);

            return this.source.reload();
        }
    }

    export interface EditorViewModelBase<T extends MappableBlock<TModel>, TModel extends MappableObject = MappableObject, TKey = any> {

        readonly isBusy: utils.IBusyCounter;
        readonly focus?: EditorFocusViewModelBase;

        getStateHandler?(): EditorStateHandlerBase<TKey>;
        getStateDesc?(): string;
        //getImageUrl?(): string;
        //getPageInfo(): IPageInfo;

        loadAdditional?<T>(editMode: EditorMode | "extern", key: TKey): Promise<void>;
        prepareSave?(editMode: EditorMode, key: TKey): Promise<void>;
        saveAdditional?<T>(editMode: EditorMode, key: TKey): Promise<void>;

        open?(key?: TKey): Promise<void>;
        delete?(key?: TKey): Promise<void>;

        errorMetadata?: IErrorMapMetadataProvider;

        initNew?(model: T, key: TKey): PromiseLike<void> | void;
    }

    export class EditorViewModelBase<T extends MappableBlock<TModel>, TModel extends MappableObject = MappableObject, TKey = any, TRep extends IDataRepository<TModel, TKey, any, T> = IDataRepository<TModel, TKey, any, T>> {

        public readonly isBusy = utils.createBusyCounter();

        private _stateHandler: EditorStateHandlerBase<TKey>;
        readonly key = ko.observable<TKey>();
        readonly model = ko.observable<T>();
        readonly editMode = ko.observable<EditorMode>("none");
        readonly errors = utils.getErrorList(true);
        readonly errorList: ErrorListWidget = new EditorErrorListWidget(this);

        readonly dialogResult = ko.observable<boolean>();
        readonly isWrite = ko.pureComputed(() => this.editMode() == "edit" || this.editMode() == "create");
        readonly isNew = ko.pureComputed(() => this.editMode() == "create" || this.editMode() == "none");
        readonly canSubmit = ko.pureComputed(() => this.editMode() != "details" && this.editMode() != "none");

        readonly onSuccess = jQuery.Callbacks<(editMode: EditorMode, key: TKey) => void>();
        readonly onFail = jQuery.Callbacks<(editMode: EditorMode, key: TKey, error: any) => void>();
        //menuCtx: IMenuContext;

        // This property needs to be set before calling getPageInfo to take effect
        hideOpenLoadActions = false;


        constructor(readonly rep: TRep, readonly func: { new(parent: any, model?: MappableObject): T }, readonly keyFunc: (vm: T | TModel) => TKey, readonly owner?: any) {

            isBusy.addDependency(this.isBusy);

            //if (owner instanceof EditorViewModelBase) {
            //    this.menuCtx = owner.menuCtx.createChildContext();
            //}
            //else {
            //    this.menuCtx = cdh.menu.targets.createChildContext();

            //}

        }

        dispose() {

            this.dialogResult(false);
            this.closeIfLoaded(false);

            this._stateHandler = null;
            this.onSuccess.disable();
            this.onFail.disable();
            //this.menuCtx.removeContext();
            isBusy.removeDependency(this.isBusy);

            this.isWrite.dispose();
            this.isNew.dispose();
            this.canSubmit.dispose();
        }

        protected createModelForNew(key?: TKey): RecursivePartial<TModel> {
            return this.rep.createNew(key);
        }

        create(replaceState?: boolean, key?: TKey) {
            return this.load(key, "create", replaceState);
        }

        async load(key: TKey, editMode?: EditorMode, replaceState?: boolean, save?: boolean): Promise<void> {
            let self = this;

            var tmp = await self.closeIfLoaded(save, replaceState);
            if (replaceState == null) replaceState = tmp;

            var p2 = loadCore();
            self.setBusy(p2, t2("data/common/loading"));

            await p2;

            async function loadCore() {
                if (!editMode || typeof editMode !== "string")
                    editMode = "edit";

                if (utils.equalsIgnoreCase(editMode, "none")) {
                    self.clear();
                }
                else if (key == null || utils.equalsIgnoreCase(editMode, "create")) {
                    self.fromModel("create", self.createModelForNew(key))

                    if (self.initNew)
                        await self.initNew(self.model(), key);

                    if (self._stateHandler)
                        self._stateHandler.setState(key, "create", undefined, replaceState);
                }
                else {
                    if (self._stateHandler)
                        self._stateHandler.setState(key, editMode, undefined, replaceState);

                    self.fromModel(editMode, await self.rep.get(key, self.errors.handler));
                }

                if (self.loadAdditional)
                    await self.loadAdditional(editMode, key);

                self.updateState();
            }
        }

        async loadExtern(model: TModel, editMode: EditorMode = "create", replaceState?: boolean, save?: boolean) {
            var self = this;
            await self.closeIfLoaded(save);

            this.dialogResult(null);

            if (!editMode || typeof editMode !== "string")
                editMode = "create";

            var p2 = loadExternCore();

            self.setBusy(p2, t2("data/common/loading"));

            await p2;

            async function loadExternCore() {

                var key = self.keyFunc(model);
                if (self._stateHandler)
                    self._stateHandler.setState(key, editMode, undefined, replaceState);

                self.fromModel(editMode, model);

                if (self.loadAdditional)
                    await self.loadAdditional("extern", key);

                self.updateState();
            }
        }

        reload(editMode?: EditorMode) {
            let k = ko.unwrap(this.key);

            let em2 = ko.unwrap(this.editMode);
            if (typeof editMode !== "string" || em2 == "create")
                editMode = em2;

            this.load(k, editMode, true, false);

        }

        clear(keepState?: boolean, keepDialog?: boolean) {
            if (this._stateHandler) {
                this._stateHandler.setState(null, "none", null, keepState);
            }

            this.fromModel("none", null);
            this.errors.handler.clear();

            if (!keepDialog)
                this.dialogResult(false);
        }

        async closeIfLoaded(save?: boolean, replaceState?: boolean): Promise<boolean> {
            let self = this;
            let s = ko.unwrap(this.isWrite);

            if (s) {
                if (typeof save !== "boolean")
                    try {
                        save = await ui.showYesNoCancelDialog(t("view/common/promptSaveChanges"));
                    }
                    catch (e) {
                        this.updateState(false);
                        throw e;
                    }

                if (save) {
                    await self.save();
                }
            }

            var res = this.editMode() != "none";
            if (res)
                this.clear(replaceState, true);

            return res;
        }

        public useStateHandler(): IEditorUrlStateHandler {
            if (this._stateHandler == null && this.getStateHandler) {
                this._stateHandler = this.getStateHandler();

                this.updateState();
            }

            return this._stateHandler;
        }

        private updateState(replaceState: boolean = true) {
            if (this._stateHandler) {

                let em = ko.unwrap(this.editMode);
                if (em != "none") {
                    let desc: string = undefined;

                    if (this.getStateDesc)
                        desc = this.getStateDesc();

                    this._stateHandler.setState(ko.unwrap(this.key), em, desc, replaceState);
                }
            }
        }


        protected fromModel(mode: EditorMode, model: RecursivePartial<TModel>) {
            var vm = ko.unwrap(this.model);

            if (vm)
                vm.fromModel(model);
            else
                this.model(vm = new this.func(this, model));

            this.editMode(mode);
            this.key(this.keyFunc(vm));
        }

        protected toModel() {
            var vm = ko.unwrap(this.model);

            if (vm)
                return vm.toModel();
            else
                return null;
        }

        protected async saveBase(setResult: boolean) {
            var self = this;
            var em = ko.unwrap(this.editMode);

            var key = ko.unwrap(this.key);

            var _t = t2(em != "delete" ? "data/common/saving" : "data/common/deleting");

            var _d = this.isBusy.beginUsing(_t);
            try {
                if (this.prepareSave)
                    await this.prepareSave(em, key);

                var m = self.toModel();
                if (m)
                    switch (em) {
                        case "none":
                        case "details":
                            return;
                        case "create":
                            var data = await self.rep.insert(m, self.errors.handler);
                            await self.fromModel("edit", data);
                            key = ko.unwrap(self.key);
                            break;
                        case "edit":
                            var data = await self.rep.update(key, m, self.errors.handler);
                            await self.fromModel("edit", data);
                            break;
                        case "delete":
                            await self.rep.delete(key, utils.promptErrorHandler);
                            self.clear();
                            break;
                        default:
                            throw new Error("EditMode:" + em);
                    }


                if (this.saveAdditional)
                    await self.saveAdditional(em, key);

                if (em != "delete")
                    key = ko.unwrap(self.key);

                self.onSuccess.fire(em, key);

                if (setResult) {
                    this.dialogResult(true);
                }

                self.updateState();
            }
            catch (err) {
                self.onFail.fire(em, key, err);
                throw err;
            }
            finally {
                _d.dispose();
            }
        }

        //protected async uploadImageBase(options?: { url?: () => string }) {
        //    if (!options) options = {};

        //    if (typeof options.url != "function") {
        //        if (this.getImageUrl)
        //            options.url = this.getImageUrl.bind(this);
        //        else
        //            throw new Error("No Image Url Function");
        //    }

        //    await this.saveBase(false);

        //    var img: File = null;
        //    // TODO Show File selection Dialog

        //    var dia = new search.FileSelectionViewModel();
        //    dia.title(t("view/master/setLogo"));
        //    dia.filter("image/jpeg,image/bmp,image/png");
        //    dia.canRenameFile(false);
        //    img = await dia.showDialog();

        //    var n = ko.unwrap(dia.uploadName);
        //    debugger;

        //    if (!(img instanceof Blob) || !utils.startsWithIgnoreCase(img.type, "image/"))
        //        throw new Error("Invalid Argument");

        //    let d = isBusy.beginUsing(t2("data/common/saving"));
        //    try {

        //        var url = options.url();

        //        var fd = new FormData();

        //        fd.append("file", img, img.name);
        //        if (n) fd.append("name", n);


        //        var req = $.ajax({
        //            url: url,
        //            method: "POST",
        //            data: fd,
        //            cache: false,
        //            contentType: false,
        //            processData: false
        //        });

        //        var res: TModel = await utils.handleAjaxErrors(req, utils.simpleErrorHandler);

        //        await this.reload();
        //    }
        //    finally {
        //        d.dispose();
        //    }
        //    //this.loadExtern(res, "edit", true, false);
        //}

        save(setResult?: boolean) {
            if (typeof setResult != "boolean")
                setResult = true;

            return this.saveBase(setResult);
        }

        protected static async prepareDelete<T, TModel, TKey>(vm: EditorViewModelBase<T, TModel, TKey>, key?: TKey) {
            if (key && key != ko.unwrap(vm.key)) {
                await vm.load(key);
            }

            let em = ko.unwrap(vm.editMode);
            key = ko.unwrap(vm.key);


            if (em === "create") {
                vm.clear();
                return false;
            }
            else if (em === "edit" || em === "details")
                return true;
            else
                return false;
        }

        protected static async deleteBase<T, TModel, TKey>(vm: EditorViewModelBase<T, TModel, TKey>, msgFunc?: string | { (): string }, key?: TKey) {

            if (!await this.prepareDelete(vm, key))
                return Promise.reject("Not loaded");
            key = vm.key();

            let title = t("view/common/delete");
            let msg: string;
            if (msgFunc) {
                if (typeof (msgFunc) === "string") msg = msgFunc;
                else if (typeof (msgFunc) === "function") msg = msgFunc();
            }
            if (!msg) msg = t("view/common/deleteEntry");

            if (await ui.showYesNoDialog(msg, title)) {
                let d = vm.isBusy.beginUsing();
                try {
                    await vm.rep.delete(key, utils.promptErrorHandler);
                }
                finally {
                    d.dispose();
                }

                vm.clear();
                vm.dialogResult(false);
                return;
            }
            else
                return Promise.reject("Cancelled by user");


        }

        setBusy(handle: Promise<any>, title?: ObservableOrValueReadonly<string>) {
            this.isBusy.setBusy(handle, title);
        }
    }

    /**
     *  ViewModel for Tracking current Focus
     */
    export class FocusViewModelBase<TParent = any> {
        focusedElement: KnockoutObservable<{}>;
        focusedVm: KnockoutObservable<any>;

        focusedEditor: KnockoutReadonlyObservable<cdh.ui.EditorControl>;
        readonly addField: <T>(evaluator: (vm: any[]) => T) => KnockoutReadonlyObservable<T>;

        constructor(public parent: TParent) {
            var self = this;

            const _focusedVm = ko.observable<any[]>();

            var _focusHandlers: { obs: KnockoutObservable<any>, evaluator: (vm: any) => any }[] = [];
            this.focusedVm = ko.computed({
                read: _focusedVm,
                write: vm => {
                    if (vm && !$.isArray(vm)) vm = [vm];
                    if (vm && !vm[0]) vm = null;

                    let cur = _focusedVm();

                    if (vm === cur || (vm && cur && vm[0] === cur[0])) return;

                    console.log("Focused VM:", vm);

                    _focusHandlers.forEach(h => FocusViewModelBase.updateIfChanged(h.obs, h.evaluator(vm)));

                    if (this.updateFocus)
                        this.updateFocus(vm);

                    _focusedVm(vm);
                }
            });

            this.focusedElement = ko.observable();

            this.addField = function <T>(evaluator: (vm: any[]) => T): KnockoutReadonlyObservable<T> {
                let obs = ko.observable<T>();

                _focusHandlers.push({ obs: obs, evaluator });
                return obs;
            }

            this.focusedEditor = this.addField(FocusViewModelBase.getEditor);

        }

        protected updateFocus?(vm: any): void;

        protected static updateIfChanged<T>(obs: KnockoutObservable<T>, val: T) {
            if (obs.peek() !== val)
                obs(val);
        }

        private static getEditor(vm: any) {
            if ($.isArray(vm)) vm = vm[0];

            if (vm == null)
                return null;
            else if (vm instanceof cdh.ui.EditorControl)
                return vm;
            else
                return null;
        }

        public listen(node: Element | JQuery) { // Q> Return a Disposable and Store Information about the Node for Focus-Actions
            var self = this;

            $(node).on("focusin", ".focus-node", function (evt) {
                var node = this;

                var data = $(node).parents(".focus-node").map((i, n) => ko.dataFor(n)).toArray();
                data.unshift(ko.dataFor(node));

                self.focusedVm(data);

                evt.stopPropagation();

                return false;
            });
        }

        protected modelAliases: string[] = ["Model"];
        protected getRoot?(): any;

        public focusElement(path: string) {
            if (!path) {
                this.focusedElement(null);
                return;
            }

            let n: any = this.getRoot ? this.getRoot() : this.parent;

            if (n != null) {
                let segArr = splitPath(path);
                //let m = path.match(FocusViewModelBase.reg);

                if (segArr.length > 0 && (segArr[0].type == "root" || segArr[0].type == "prop") && this.modelAliases.some(a => utils.equalsIgnoreCase(segArr[0].path, a)))
                    segArr.shift();

                //if (m.length > 0 && this.modelAliases.some(a => utils.equalsIgnoreCase(m[0], a)))
                //    m.shift();

                for (let mIndex = 0; mIndex < segArr.length; mIndex++) {
                    let seg = segArr[mIndex];
                    let val = seg.path;

                    if (n == null)
                        break;

                    let tmp = n;
                    let tr: IErrorMapTraceable = null;

                    if ((typeof (tmp) == "object" || typeof (tmp) == "function") && "getNodeForErrorPath" in tmp) {
                        tr = tmp;
                    }
                    else if (ko.isObservable(tmp)) {
                        tmp = tmp();

                        if (tmp != null && (typeof (tmp) == "object" || typeof (tmp) == "function") && "getNodeForErrorPath" in tmp) {
                            tr = tmp;
                        }
                    }


                    if (tr)
                        tmp = tr.getNodeForErrorPath(seg);
                    else
                        tmp = getNodeForPath(tmp, seg);

                    n = tmp;

                }
            }

            this.focusedElement(n);
        }

        public focusNode(nodeVms: object[]) {
            if (!nodeVms || !nodeVms.length) {
                this.focusElement(null);
                return
            }

            var fNodes = $(".focus-node").map((ix, elm) => ({ elm: elm, data: ko.dataFor(elm) })).toArray();

            for (var i = nodeVms.length - 1; i >= 0; i--) {
                var n = nodeVms[i];

                var tmp = fNodes.find(n2 => n2.data === n);
                if (tmp) {
                    if (tmp.elm.tagName == "DIV" || tmp.elm.tabIndex < 0) {
                        // TODO Find Focusable Element
                        $(tmp.elm).find("input:visible").first().focus();
                    }
                    else
                        tmp.elm.focus();




                    return;
                }

            }


        }

        private static reg = /$([a-z0-9_]+)|(?!\.)([a-z0-9_]+)|(?!\[)([a-z0-9_]+)(?!\])/ig;
    }

    /**
     *  ViewModel for Tracking current Focus
     */
    export class EditorFocusViewModelBase<TVM = any> extends FocusViewModelBase<EditorViewModelBase<TVM>> {
        focusedModel: KnockoutReadonlyComputed<TVM>;

        constructor(public parent: EditorViewModelBase<TVM>) {
            super(parent);
            var self = this;

            this.focusedModel = ko.computed(() => {
                var em = ko.unwrap(parent.editMode);

                if (em == "none") return null;
                else return ko.unwrap(parent.model);
            }).extend({ deferred: true });

            this.focusedModel.subscribe(() => self.focusedVm(null));
        }

        protected modelAliases: string[] = ["Model"];
        protected getRoot() { return this.parent.model(); }

    }

    export class ErrorListWidget implements IWidgetDefinition {

        getFocus?(): EditorFocusViewModelBase;

        constructor(readonly parent: any, readonly errors: KnockoutReadonlyObservable<IMessage>, focus?: EditorFocusViewModelBase | { (): EditorFocusViewModelBase }) {

            if (focus) {
                if (focus instanceof EditorFocusViewModelBase)
                    this.getFocus = () => focus;
                else if (typeof (focus) == "function")
                    this.getFocus = focus;
            }
        }

        title = t2("view/common/errorList");
        visible = ko.pureComputed(() => { let e = ko.unwrap(this.errors); return !!e });

        readonly template: DialogTemplate = { type: 'id', value: "error-list" };

        viewModel = this;

        focusElement(name: string) {
            if (this.getFocus && name && typeof name == "string") {

                var f = this.getFocus();

                if (!f) return;
                f.focusElement(name);
            }
        }

        protected errorMap = ko.pureComputed(() => {

            var md = this.getErrorMetadata ? this.getErrorMetadata() : null;
            if (!md) return null;

            var root: ErrorMapEntryBuilder = { name: "model", path: "model", segments: ["model"], errors: [], children: new utils.SimpleStringDictionary() };

            var errs = this.errors();
            if (errs) {


                if (errs.messages) {
                    errs.messages.forEach(msg => {
                        if (!msg.value) return;
                        var node = findMapEntry(root, msg.key);


                        node.errors.push(...msg.value);

                    });

                }

            }

            var res = new ErrorMapEntry(root);

            md.fill(res);

            return res;

        });

        protected getErrorMetadata?(): IErrorMapMetadataProvider;
    }

    type pathSegment = {
        path: string;
        type: "root" | "prop" | "indexer";
    }


    export function splitPath(path: string): pathSegment[] {
        var res: pathSegment[] = [];

        var curDelimiters: string[] = [];
        var start = 0;
        for (var i = 0; i < path.length; i++) {
            var c = path.charAt(i);

            switch (c) {
                case ".":
                    if (curDelimiters.length == 0) {
                        pushPart(i);
                        start = i + 1;
                    }
                    break;
                case "[":
                    if (curDelimiters.length == 0) {
                        pushPart(i);
                        start = i + 1;
                    }
                    curDelimiters.push("[");
                    break;
                case "]":
                    if (curDelimiters.length == 0 || curDelimiters[curDelimiters.length - 1] != "[")
                        throw new Error("Syntax error");
                    curDelimiters.shift();
                    if (curDelimiters.length == 0) {
                        pushPart(i, "indexer");
                        start = i + 1;
                    }
                    break;

                case "(":
                case ")":
                    throw new Error("Not Supported");
            }

        }
        if (curDelimiters.length == 0)
            pushPart(path.length);
        else
            throw new Error("Syntax Error");

        return res;

        function pushPart(end: number, type: "prop" | "indexer" = "prop") {
            var l = end - start;
            if (l > 0)
                res.push({
                    path: path.substr(start, l),
                    type: type
                });
        }

    }

    export function getNodeForPath(tmp: any, seg: pathSegment): any {
        if (seg && seg.path) {

            var val = seg.path;

            if (tmp != null) {
                if (seg.type == "indexer" && $.isNumeric(val)) {
                    let i = parseInt(val);

                    tmp = tmp[i];
                }
                else {
                    tmp = tmp[utils.firstToLowerCase(val)];
                }
            }
        }

        return tmp;
    }

    function findMapEntry(root: ErrorMapEntryBuilder, path: string) {
        if (!path)
            return root;

        var parts = splitPath(path);
        if (parts.length && utils.equalsIgnoreCase(parts[0].path, root.name))
            parts.shift();

        var node = root;
        for (var i = 0; i < parts.length; i++) {
            var part = parts[i];
            var p = part.path;

            var [found, node2] = node.children.tryGet(p);
            if (!found) {
                var s = node.segments ? node.segments.slice() : [];
                s.push(p);

                node2 = {
                    name: p,
                    path: node.path + (part.type != "indexer" ? ("." + p) : ("[" + p + "]")),
                    segments: s,

                    errors: [],
                    children: new utils.SimpleStringDictionary()
                };
                node.children.add(p, node2);
            }

            node = node2;

        }

        return node;
    }

    type ErrorMapEntryBuilder = {
        readonly name: string;
        readonly path: string;
        readonly segments: string[];

        errors: ModelStateError[]

        readonly children: utils.SimpleStringDictionary<ErrorMapEntryBuilder>;
    }

    export class ErrorMapEntry {
        readonly name: string;
        readonly path: string;
        readonly segments: string[];

        readonly errors: ModelStateError[]
        readonly children: utils.IReadonlyDictionary<string, ErrorMapEntry>;

        // Path Metadata
        static readonly pathSeparator = ko.observable("-");
        readonly displayName = ko.observable<string>();
        readonly isHidden = ko.observable(false);
        readonly isBlock = ko.observable(false);


        // Calculated Values
        readonly caption = ko.pureComputed(() => this.displayName() || this.name);
        readonly displaySegments = ko.pureComputed(() => {
            var res: string[] = this.parent ? this.parent.displaySegments().slice() : [];

            if (!this.isHidden())
                res.push(this.caption());

            return res;

        });
        readonly displayPath = ko.pureComputed(() => {
            var seg = this.displaySegments();
            if (!seg || seg.length == 0)
                return "";
            else if (seg.length == 1)
                return seg[0];
            else {
                var sep = ko.unwrap(ErrorMapEntry.pathSeparator) || "";

                if (sep.length == 0 || sep.charAt(0) != " ")
                    sep = " " + sep;
                if (sep.charAt(sep.length - 1) != " ")
                    sep += " ";

                return seg.join(sep);
            }
        });

        constructor(builder: ErrorMapEntryBuilder, readonly parent?: ErrorMapEntry) {
            var self = this;

            this.name = builder.name;
            this.path = builder.path;
            this.segments = builder.segments;


            this.errors = builder.errors ? builder.errors.slice() : [];

            var children = builder.children.getAll().map(c => new ErrorMapEntry(c, self));
            var childDict = new utils.SimpleStringDictionary<ErrorMapEntry>();
            for (var i = 0; i < children.length; i++) {
                childDict.add(children[i].name, children[i]);
            }
            this.children = childDict.toReadonly();

            if (!parent) {
                this.isHidden(true);
                this.isBlock(true);
            }
        }


        flatten() {
            var res: ErrorMapEntry[] = [];

            var nodes: ErrorMapEntry[] = [this];
            while (nodes.length) {
                let node = nodes.shift();
                nodes.push(...node.children.getAll());

                if (node.errors.length)
                    res.push(node);
            }

            return res;
        }

        groupBlocks() {
            var blocks: { block: ErrorMapEntry, entries: ErrorMapEntry[] }[] = [];

            var nodes: { node: ErrorMapEntry, block: { block: ErrorMapEntry, entries: ErrorMapEntry[] } }[] =
                [{ node: this, block: null }];

            while (nodes.length) {
                var { node, block } = nodes.shift();

                if (block == null || node.isBlock()) {
                    block = { block: node, entries: [] };
                    blocks.push(block);
                }

                if (node.errors.length)
                    block.entries.push(node);

                var cArr = node.children.getAll();
                for (var i = 0; i < cArr.length; i++) {
                    var c = cArr[i];
                    nodes.push({ node: c, block: block });
                }

            }

            blocks = blocks.filter(b => b.entries.length);

            return blocks;
        }

        flattenBlocks() {


            var blocks = this.groupBlocks();


            return blocks;
        }


    }

    export class EditorErrorListWidget extends ErrorListWidget {

        constructor(readonly parent: EditorViewModelBase<any>) {
            super(parent, parent.errors, () => parent.focus);


        }

        getErrorMetadata() {
            return this.parent.errorMetadata;
        }
    }

    export interface IErrorMapMetadataProvider {
        fill(entry: ErrorMapEntry): void;
    }

    export interface IErrorMapTraceable {
        getNodeForErrorPath(segment: pathSegment): any;
    }

    export abstract class ErrorMapMetadataProviderBase<T = unknown> implements IErrorMapMetadataProvider {
        private _children = new utils.SimpleStringDictionary<IErrorMapMetadataProvider>();
        private readonly _commonNames: utils.SimpleStringDictionary<IErrorMapMetadataProvider>;

        constructor(readonly parent?: ErrorMapMetadataProviderBase) {
            if (parent)
                this._commonNames = parent._commonNames;
            else
                this._commonNames = new utils.SimpleStringDictionary();
        }

        protected abstract fillEntry(entry: ErrorMapEntry): void;

        protected fillChildren(parent: ErrorMapEntry): void {

            var cArr = parent.children.getAll();
            for (var i = 0; i < cArr.length; i++) {
                var c = cArr[i];
                var meta = this._children.get(c.name);
                if (!meta)
                    meta = this._commonNames.get(c.name);

                if (meta)
                    meta.fill(c);
            }
        }

        fill(entry: ErrorMapEntry): void {

            this.fillEntry(entry);
            this.fillChildren(entry);
        }

        addProperty<K extends Extract<keyof T, string>, TProp = T[K]>(name: K, disp?: ObservableOrValueReadonly<string>): ErrorMapMetadataProvider<TProp>;
        addProperty<TProp = unknown>(name: string, disp?: ObservableOrValueReadonly<string>): ErrorMapMetadataProvider<TProp>;
        addProperty<TProp = unknown>(name: string, disp?: ObservableOrValueReadonly<string>): ErrorMapMetadataProvider<TProp> {
            var res = new ErrorMapMetadataProvider(name, this);
            res.displayName = disp;

            this._children.add(name, res);

            return res;
        }

        addIndexerProperty<TProp = unknown>(name: Extract<keyof T, string>, disp?: ObservableOrValueReadonly<string>): ErrorMapMetadataIndexerProvider<TProp>;
        addIndexerProperty<TProp = unknown>(name: string, disp?: ObservableOrValueReadonly<string>): ErrorMapMetadataIndexerProvider<TProp>;
        addIndexerProperty<TProp = unknown>(name: string, disp?: ObservableOrValueReadonly<string>): ErrorMapMetadataIndexerProvider<TProp> {
            var res = new ErrorMapMetadataIndexerProvider(name, this);
            res.displayName = disp;

            this._children.add(name, res);

            return res;
        }

        addNames(names: { [key in Extract<keyof T, string>]?: ObservableOrValueReadonly<string> }): void;
        addNames(names: Dictionary<ObservableOrValueReadonly<string>>): void;
        addNames(names: Dictionary<ObservableOrValueReadonly<string>>): void {
            var self = this;

            if (names)
                $.each(names, (name, value) => {
                    if (name && typeof (name) == "string")
                        self.addProperty(name, value);
                });

        }


        addCommonProperty<TProp = unknown>(name: string, disp?: ObservableOrValueReadonly<string>): ErrorMapMetadataProvider<TProp> {
            var res = new ErrorMapMetadataProvider(name, this);
            res.displayName = disp;

            this._commonNames.add(name, res);

            return res;
        }

        addCommonNames(names: Dictionary<ObservableOrValueReadonly<string>>): void {
            var self = this;

            if (names)
                $.each(names, (name, value) => {
                    if (name && typeof (name) == "string")
                        self.addCommonProperty(name, value);
                });

        }

    }

    export class ErrorMapMetadataProvider<T = unknown> extends ErrorMapMetadataProviderBase<T> {

        isBlock: boolean = false;
        isHidden: boolean = false;
        displayName: ObservableOrValueReadonly<string>;

        constructor(readonly name: string, parent?: ErrorMapMetadataProviderBase) {
            super(parent);
        }

        fillEntry(entry: ErrorMapEntry) {

            entry.displayName(ko.unwrap(this.displayName));
            entry.isBlock(this.isBlock);
            entry.isHidden(this.isHidden);
        }
    }

    export class ErrorMapMetadataIndexerProvider<T = unknown> extends ErrorMapMetadataProvider<T> {

        isBlock: boolean = false;
        isHidden: boolean = false;


        isBlockChild: boolean = false;
        isHiddenChild: boolean = false;
        indexerNameFunc: (name: string) => string;

        constructor(readonly name: string, parent?: ErrorMapMetadataProviderBase) {
            super(name, parent);
        }

        fill(entry: ErrorMapEntry) {

            this.fillEntry(entry);

            var cArr = entry.children.getAll();
            for (var i = 0; i < cArr.length; i++) {
                var c = cArr[i];

                if (this.indexerNameFunc)
                    c.displayName(this.indexerNameFunc(c.name));
                c.isBlock(this.isBlockChild);
                c.isHidden(this.isHiddenChild);

                this.fillChildren(c);

            }

        }
    }

    export class PageInfoActionBuilderBase {
        private readonly _gCreate = ko.observableArray<IActionDefinition>();
        private readonly _gOpen = ko.observableArray<IActionDefinition>();
        private readonly _gEdit = ko.observableArray<IActionDefinition>();
        private readonly _gDelete = ko.observableArray<IActionDefinition>();
        private readonly _gOther = ko.observableArray<IActionDefinition>();

        clear() {
            this._gCreate.removeAll();
            this._gOpen.removeAll();
            this._gEdit.removeAll();
            this._gDelete.removeAll();
            this._gOther.removeAll();
        }

        getActions(): IActionGroupDefinition[] {
            var a: IActionGroupDefinition[] = [];
            let i: IActionDefinition[];

            // Q> Create Builder-Class for Group (and Action) Definitions?
            // Then Add Properties for Default Groups and a Method for setting the Default Values

            // Create
            i = ko.unwrap(this._gCreate);
            if (i && i.length)
                a.push({ color: "success", items: i });

            // Open
            i = ko.unwrap(this._gOpen);
            if (i && i.length)
                a.push({ items: i });

            // Save/Cancel/Reload
            i = ko.unwrap(this._gEdit);
            if (i && i.length)
                a.push({ items: i });

            // Delete
            i = ko.unwrap(this._gDelete);
            if (i && i.length)
                a.push({ color: "danger", items: i });

            // Other
            i = ko.unwrap(this._gOther);
            if (i && i.length)
                a.push({ collapse: true, icon: "fa fa-cogs", items: i });// Q> Caption?

            return a;
        }

        addAction(name: "create" | "edit" | "open" | "delete" | "other", action: IActionDefinition) {
            let group = this._gOther;

            switch (name) {
                case "create":
                    group = this._gCreate;
                    break;
                case "edit":
                    group = this._gEdit;
                    break;
                case "open":
                    group = this._gOpen;
                    break;
                case "delete":
                    group = this._gDelete;
                    break;
            }

            if (group && action)
                group.push(action);

        }


    }

    export class EditorPageInfoHelper<TEditor extends EditorViewModelBase<any, any>> extends PageInfoActionBuilderBase {
        readonly noData: KnockoutComputed<boolean>;
        readonly noWrite: KnockoutComputed<boolean>;
        readonly noNew: KnockoutComputed<boolean>;
        readonly existing: KnockoutComputed<boolean>;
        readonly notExisting: KnockoutComputed<boolean>;

        constructor(public readonly editor: TEditor) {
            super();

            this.noData = ko.pureComputed(() => this.editor.editMode() == "none");
            this.existing = ko.pureComputed(() => this.editor.editMode() == "edit" || this.editor.editMode() == "details");
            this.notExisting = ko.pureComputed(() => !this.existing());
            this.noWrite = ko.pureComputed(() => !this.editor.canSubmit());
            this.noNew = ko.pureComputed(() => !this.editor.isNew());

            this.fillDefaultActions();
        }

        fillDefaultActions(clear = true) {
            if (clear !== false) {
                this.clear();
            }

            let e: EditorViewModelBase<any> = this.editor;

            if (e.hideOpenLoadActions != true) {
                // Create
                this.addAction("create", { caption: t2("view/common/create"), icon: "fa-plus", color: "success", click: () => e.create() });

                // Open
                if (e.open) {
                    this.addAction("open", { caption: t2("view/common/open"), icon: "fa-folder-open", click: () => e.open() });

                }
            }

            // Save/Cancel/Reload
            this.addAction("edit", { caption: t2("view/common/save"), icon: "fa-save", disable: this.noWrite, click: () => e.save() });
            this.addAction("edit", { caption: t2("view/common/cancel"), icon: "fa-times", disable: this.noData, click: () => e.clear() });
            this.addAction("edit", { caption: t2("view/common/reload"), icon: "fa-redo-alt", disable: this.noData, click: () => e.reload() });


            if (e.hideOpenLoadActions != true) {
                // Delete
                if (e.delete)
                    this.addAction("delete", { caption: t2("view/common/delete"), icon: "fa-trash", color: "danger", disable: e.isNew, click: () => e.delete() });
            }
        }

    }

    export function bindDatasourceToEditor<TKey>(ds: IDataSourceViewModel<any, TKey>, editor: EditorViewModelBase<any, any, TKey>) {

        if (editor) {
            ds.commandHandler.addHandlersStart(new EditorCommandHandler(editor));
            editor.onSuccess.add((em, key) => {
                ds.source.reload();
            });

            bindDatasourceToCallbackCmdHandler(ds, new DeleteCommandHandler(editor.rep));
        }
    }

    export function bindDatasourceToCallbackCmdHandler<TKey>(ds: IDataSourceViewModel<any, TKey>, cmd: ISuccessCallbackCommandHandler<TKey>) {

        if (cmd) {
            ds.commandHandler.addHandlersStart(cmd);
            cmd.onSuccess.add(() => {
                ds.source.reload();
            });
        }
    }

    export type EditorState<TKey> = { name: string, key: TKey, mode: EditorMode };

    export interface IEditorUrlStateHandler<TKey = unknown> {
        handlePopState(ev: PopStateEvent): void;

        setState(key: TKey, mode: EditorMode, desc?: string, replace?: boolean): void;
    }

    export abstract class EditorStateHandlerBase<TKey> implements IEditorUrlStateHandler<TKey>, IPopStateHandler {


        static readonly isSupported = utils.pushStateSupported;

        constructor(readonly editor: EditorViewModelBase<any, any, TKey>, readonly name: string) {
        }

        onOtherData?(ev: PopStateEvent): void;

        handlePopState(ev: PopStateEvent) {
            var data = <Partial<EditorState<TKey>>>ev.state

            // Todo Check PopStateEvent-Handling

            // check if the Event can be handled by the Editor.
            if (!this.tryHandlePopState(ev)) {
                // I don't know whether this State can even occur
                if (this.onOtherData) {
                    this.onOtherData(ev);
                    return;
                }

                document.location.reload();
            }

        }

        tryHandlePopState(ev: PopStateEvent) {
            var data = <Partial<EditorState<TKey>>>ev.state

            // Todo Check PopStateEvent-Handling

            // check if the Event can be handled globally
            if (data && utils.equalsIgnoreCase(data.name, this.name) && data.mode) {
                this.editor.load(data.key, data.mode, true).then(undefined, err => {
                    this.editor.clear();
                });
                return true;
            }
            return false;
        }

        setState(key: TKey, mode: EditorMode, desc?: string, replace?: boolean) {
            if (EditorStateHandlerBase.isSupported) {
                var url = this.getUrl(key, mode);

                var state: EditorState<TKey> = { name: this.name, key, mode };

                if (!desc)
                    desc = mode + " - " + key;

                console.log("stateHandler", document.title, url, desc, replace);
                utils.pushState(url, state, desc, replace);
            }

        }

        abstract getUrl(key: TKey, mode: EditorMode): string;

    }
    // #endregion

}